import type { LibraryFont } from '../services/fonts';

/*
 * A font sent from Flobi Fonts (cross-app pages, 29 Oct 2026): the parcel's faces, by their Fonts ids, go into a file's
 * fonts. The page asks "a new file or one of yours" and opens the editor with ?fonts=<id~format,…>&family=<name>; the
 * editor writes them as the font picker writes a library family (`resolveLibraryFamily`): every face under the
 * family's own name, so Bold is there to choose. Nothing is set on a layer: the family is then in the Font picker.
 */

/** One face as a font parcel carries it (Flobi Fonts' Send to › Use in). */
export interface FontParcelItem { mediaAssetId?: string; fontId?: string; name?: string; mime?: string; family?: string; style?: string; weight?: number }
/** A face the editor asks for: its Fonts id and its file's format. */
export interface SentFace { id: string; format: 'ttf' | 'otf' }

/**
 * The faces a parcel carries: `payload.fonts` as Fonts writes it (the kit's parcel reader keeps only an item's file
 * fields, so `fontId` and `family` are not on `items`), else the items themselves.
 */
export function fontItemsOf(parcel: { items?: unknown; payload?: unknown }): FontParcelItem[] {
  const fonts = (parcel.payload as { fonts?: unknown } | null | undefined)?.fonts;
  const list = Array.isArray(fonts) ? fonts : Array.isArray(parcel.items) ? parcel.items : [];
  return list.filter((item): item is FontParcelItem => !!item && typeof item === 'object');
}

/** Artwork draws TTF and OTF only (the picker says so too). */
const EXT = /\.(ttf|otf)$/i;
const MIME = /^(font\/(ttf|otf|sfnt)|application\/(x-)?font-(ttf|otf|truetype|opentype|sfnt))$/i;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The file's format: by its name, else by its type (an OpenType type is OTF). */
const formatOf = (item: FontParcelItem): 'ttf' | 'otf' | null => {
  const ext = EXT.exec(item.name ?? '')?.[1]?.toLowerCase();
  if (ext === 'ttf' || ext === 'otf') return ext;
  if (!MIME.test(item.mime ?? '')) return null;
  return /otf|opentype/i.test(item.mime ?? '') ? 'otf' : 'ttf';
};

export const fits = (item: FontParcelItem) => UUID.test(item.fontId ?? '') && formatOf(item) !== null;

/** What the page lists: each face, the ones Artwork can't draw marked. */
export function fontRows(items: readonly FontParcelItem[]) {
  return items.map((item, i) => ({
    id: item.fontId || item.mediaAssetId || String(i),
    name: [item.family, item.style].filter(Boolean).join(' ') || item.name || 'Font',
    glyph: 'doc',
    ...(fits(item) ? { tag: formatOf(item)!.toUpperCase() } : { tag: 'Left out', muted: true }),
  }));
}

/** The faces Artwork takes, once each, and how many are left out. */
export function sentFaces(items: readonly FontParcelItem[]): { faces: SentFace[]; leftOut: number } {
  const faces: SentFace[] = [];
  for (const item of items) if (fits(item) && !faces.some((f) => f.id === item.fontId)) faces.push({ id: item.fontId!, format: formatOf(item)! });
  return { faces, leftOut: items.length - faces.length };
}

/** The families, for the page's words: "Inter", "Inter, Lora". */
export const familyNames = (items: readonly FontParcelItem[]) => [...new Set(items.filter(fits).map((item) => item.family || item.name?.replace(EXT, '') || 'Font'))];

/** ?fonts= as the editor reads it: `<Fonts id>~<format>`, comma-separated; anything else in it is dropped. */
export const fontsParam = (faces: readonly SentFace[]) => faces.filter((f) => UUID.test(f.id)).map((f) => `${f.id}~${f.format}`).join(',');
export function facesFromParam(value: string | null): SentFace[] {
  return (value ?? '')
    .split(',')
    .map((pair) => {
      const [id = '', format = ''] = pair.trim().split('~');
      return { id, format: format.toLowerCase() === 'otf' ? ('otf' as const) : ('ttf' as const) };
    })
    .filter((f) => UUID.test(f.id));
}

/** The faces as the font picker hands them to `resolveLibraryFamily` (it reads each one's id and format). */
export function libraryFaces(faces: readonly SentFace[], family: string): LibraryFont[] {
  return faces.map((f) => ({ id: f.id, format: f.format, family, style: '', weight: 400, italic: false, visibility: '', classification: null, brands: null, projects: null }));
}
